import React, { useEffect, useState } from 'react';
import { Card, Button, Row, Col, Container } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaPhone,
  FaSpinner, 
  FaUser, 
  FaCheckCircle,
  FaArrowLeft,
  FaEnvelope,
  FaDollarSign,
  FaTools,
  FaPhoneVolume,
  FaInfoCircle
} from 'react-icons/fa';
import { leadsAPI, callsAPI } from '../services/api';
import { toast } from 'react-toastify';
import '../styles/LiveCall.css';

// const getStatusGradient = (status) => {
//   switch (status?.toLowerCase()) {
//     case 'new':
//       return 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
//     case 'in_progress':
//     case 'in_call':
//       return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
//     case 'completed':
//     case 'contacted':
//       return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
//     case 'no_answer':
//     case 'missed':
//     case 'remainder':
//       return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
//     default:
//       return 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)';
//   }
// };

const LiveCall = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitiating, setIsInitiating] = useState(false);
  const [callInitiated, setCallInitiated] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await leadsAPI.getLead(leadId);
        setLead(response.data);
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to load lead:', error);
        toast.error('Failed to load lead data');
        navigate('/leads');
      }
    };

    fetchData();
  }, [leadId, navigate]);

  const handleInitiateCall = async () => {
    setIsInitiating(true);
    
    console.log('Initiating call for lead:', leadId);
    console.log('Lead details:', lead);
    
    try {
      console.log('Calling API: /calls/initiate/ with lead_id:', leadId);
      const response = await callsAPI.initiateCall(leadId);
      
      console.log('API Response:', response.data);
      
      if (response.data.status === 'ongoing' || response.data.status === 'in_progress' || response.data.elevenlabs_conversation_id) {
        setCallInitiated(true);
        toast.success(`Call initiated to ${lead.name}! ElevenLabs is calling ${lead.phone} now.`);
      } else if (response.data.status === 'failed') {
        toast.error('Failed to initiate call: ' + (response.data.error || 'Unknown error'));
      } else {
        // Even if status is not as expected, if we have a conversation ID, it's likely successful
        setCallInitiated(true);
        toast.success(`Call initiated to ${lead.name}!`);
      }
    } catch (error) {
      console.error('Error initiating call:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Failed to initiate call');
    } finally {
      setIsInitiating(false);
    }
  };

  const handleBackToLeads = () => {
      navigate('/leads');
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <FaSpinner className="spinner" size={40} />
      </div>
    );
  }

  if (!lead) return null;

  return (
    <Container fluid className="live-call-container p-4" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* Back Button */}
      <Button 
        variant="link" 
        className="mb-4 text-decoration-none"
        onClick={handleBackToLeads}
        style={{ 
          color: '#64748b',
          fontWeight: '500',
          fontSize: '0.95rem',
          padding: '0'
        }}
      >
        <FaArrowLeft className="me-2" />
        Back to Leads
      </Button>

      <Row className="g-4">
        {/* Main Call Card */}
        <Col lg={7}>
          <Card 
            className="border-0 shadow-sm h-100"
            style={{ 
              borderRadius: '16px',
              background: callInitiated 
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <Card.Body className="text-center p-5 text-white">
              {/* Avatar with pulse animation */}
              <div className="position-relative d-inline-block mb-4">
                <div 
                  className={callInitiated ? 'pulse-animation' : ''}
                  style={{ 
                    width: '120px', 
                    height: '120px', 
                    borderRadius: '50%', 
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '3px solid rgba(255, 255, 255, 0.25)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {callInitiated ? (
                    <FaPhoneVolume size={50} color="white" />
                  ) : (
                    <FaUser size={50} color="white" />
                  )}
                </div>
                {callInitiated && (
                  <div 
                    className="position-absolute"
                    style={{
                      top: '0px',
                      right: '0px',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      animation: 'pulse 2s ease-in-out infinite'
                    }}
                  >
                    <FaCheckCircle size={16} color="#10b981" />
                  </div>
                )}
              </div>

              <h1 className="fw-bold mb-2" style={{ fontSize: '2rem' }}>{lead.name}</h1>
              <h5 className="fw-normal mb-4 opacity-90" style={{ fontSize: '1.1rem', letterSpacing: '0.5px' }}>{lead.phone}</h5>

              {!callInitiated ? (
                <>
                  <Button
                    size="lg"
                    onClick={handleInitiateCall}
                    disabled={isInitiating}
                    className="px-5 py-3 fw-semibold"
                    style={{
                      background: 'white',
                      color: '#10b981',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '1.1rem',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                      transition: 'all 0.2s ease',
                      minWidth: '220px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
                    }}
                  >
                    {isInitiating ? (
                      <>
                        <FaSpinner className="spin me-2" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <FaPhone className="me-2" />
                        Initiate Call
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <div 
                    className="mb-4 p-4"
                    style={{
                      background: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.25)'
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-center mb-2">
                      <FaCheckCircle className="me-2" size={28} />
                      <strong style={{ fontSize: '1.3rem' }}>Call In Progress</strong>
                    </div>
                    <p className="mb-0 text-center" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.95 }}>
                      AI agent is calling <strong>{lead.name}</strong>
                    </p>
                  </div>

                  <p className="mb-4 opacity-90 text-center" style={{ fontSize: '0.95rem' }}>
                    Check <strong>Call History</strong> for details
                  </p>

                  <Button
                    size="lg"
                    onClick={handleBackToLeads}
                    className="px-5 py-3 fw-semibold"
                    style={{
                      background: 'white',
                      color: '#10b981',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '1.1rem',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                      minWidth: '220px'
                    }}
                  >
                    <FaArrowLeft className="me-2" />
                    Back to Leads
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Lead Information Card */}
        <Col lg={5}>
          <Card 
            className="border-0 shadow-sm h-100"
            style={{ 
              borderRadius: '16px',
              border: '1px solid #e2e8f0'
            }}
          >
            <Card.Header 
              className="border-0 py-4 px-4"
              style={{ 
                background: '#ffffff',
                borderRadius: '16px 16px 0 0',
                borderBottom: '1px solid #f1f5f9'
              }}
            >
              <h5 className="mb-0 fw-semibold" style={{ color: '#1e293b', fontSize: '1.1rem' }}>
                <FaUser className="me-2" style={{ color: '#667eea', fontSize: '1rem' }} />
                Lead Information
              </h5>
            </Card.Header>
            <Card.Body className="p-4" style={{ background: '#fafbfc' }}>
              <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #e2e8f0' }}>
                <div className="d-flex align-items-center mb-2">
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    background: '#eff6ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '10px'
                  }}>
                    <FaUser style={{ color: '#3b82f6', fontSize: '0.8rem' }} />
                  </div>
                  <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Name</span>
                </div>
                <p className="mb-0 fw-semibold" style={{ fontSize: '1rem', color: '#1e293b', paddingLeft: '38px' }}>
                  {lead.name}
                </p>
              </div>

              <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #e2e8f0' }}>
                <div className="d-flex align-items-center mb-2">
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    background: '#f0fdf4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '10px'
                  }}>
                    <FaPhone style={{ color: '#10b981', fontSize: '0.75rem' }} />
                  </div>
                  <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Phone</span>
                </div>
                <p className="mb-0 fw-semibold" style={{ fontSize: '1rem', color: '#1e293b', paddingLeft: '38px', fontFamily: 'monospace' }}>
                  {lead.phone}
                </p>
              </div>

              {lead.email && (
                <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <div className="d-flex align-items-center mb-2">
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      background: '#eff6ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px'
                    }}>
                      <FaEnvelope style={{ color: '#3b82f6', fontSize: '0.75rem' }} />
                    </div>
                    <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</span>
                  </div>
                  <p className="mb-0" style={{ fontSize: '0.9rem', color: '#475569', paddingLeft: '38px', wordBreak: 'break-word' }}>
                    {lead.email}
                  </p>
                </div>
              )}
            
              {lead.context && (
                <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <div className="d-flex align-items-center mb-2">
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      background: '#f8fafc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px'
                    }}>
                      <FaInfoCircle style={{ color: '#64748b', fontSize: '0.75rem' }} />
                    </div>
                    <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Context</span>
                  </div>
                  <p className="mb-0" style={{ fontSize: '0.9rem', color: '#475569', lineHeight: '1.6', paddingLeft: '38px' }}>
                    {lead.context}
                  </p>
                </div>
              )}

              {lead.project_type && (
                <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <div className="d-flex align-items-center mb-2">
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      background: '#fef3c7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px'
                    }}>
                      <FaTools style={{ color: '#f59e0b', fontSize: '0.75rem' }} />
                    </div>
                    <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Project Type</span>
                  </div>
                  <p className="mb-0 fw-semibold" style={{ fontSize: '1rem', color: '#1e293b', paddingLeft: '38px', textTransform: 'capitalize' }}>
                    {lead.project_type}
                  </p>
                </div>
              )}

              {lead.proposed_price && (
                <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <div className="d-flex align-items-center mb-2">
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      background: '#d1fae5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px'
                    }}>
                      <FaDollarSign style={{ color: '#10b981', fontSize: '0.75rem' }} />
                    </div>
                    <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Proposed Price</span>
                  </div>
                  <p className="mb-0 fw-bold" style={{ fontSize: '1.25rem', color: '#10b981', paddingLeft: '38px' }}>
                    ${parseFloat(lead.proposed_price).toLocaleString()}
                  </p>
                </div>
              )}

              {lead.project_description && (
                <div className="mb-0">
                  <div className="d-flex align-items-center mb-2">
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      background: '#f8fafc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px'
                    }}>
                      <FaInfoCircle style={{ color: '#64748b', fontSize: '0.75rem' }} />
                    </div>
                    <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Project Description</span>
                  </div>
                  <p className="mb-0" style={{ fontSize: '0.9rem', color: '#475569', lineHeight: '1.6', paddingLeft: '38px' }}>
                    {lead.project_description}
                  </p>
                </div>
              )}
              
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Custom CSS for pulse animation */}
      <style>{`
        .pulse-animation {
          animation: pulse-ring 2s ease-in-out infinite;
        }
        
        @keyframes pulse-ring {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.5);
          }
          50% {
            box-shadow: 0 0 0 20px rgba(255, 255, 255, 0);
          }
        }
        
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
        }
      `}</style>
    </Container>
  );
};

export default LiveCall; 
